import * as signalR from '@microsoft/signalr';
import config from '../config';

const { api: apiConfig } = config;

/**
 * SignalR service for real-time document upload progress updates.
 * Manages connection to DocumentUploadHub and provides event subscriptions.
 */
class DocumentUploadSignalRService {
  private connection: signalR.HubConnection | null = null;
  private connectionPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Initialize and start the SignalR connection.
   * Automatically reconnects on disconnect.
   */
  async startConnection(): Promise<void> {
    // If already connected or connecting, return existing promise
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Get auth token
    const authUser = sessionStorage.getItem('authUser');
    let token = '';
    if (authUser) {
      const user = JSON.parse(authUser);
      token = user?.token || '';
    }

    // Build connection with automatic reconnect
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiConfig.API_URL}/hubs/document-upload`, {
        accessTokenFactory: () => token,
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, 60s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          if (retryContext.previousRetryCount === 3) return 30000;
          return 60000;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Handle reconnecting
    this.connection.onreconnecting((error) => {
      console.warn('DocumentUploadHub reconnecting...', error);
    });

    // Handle reconnected
    this.connection.onreconnected((connectionId) => {
      console.log('DocumentUploadHub reconnected. ConnectionId:', connectionId);
      this.reconnectAttempts = 0;
    });

    // Handle close
    this.connection.onclose(async (error) => {
      console.error('DocumentUploadHub connection closed', error);

      // Attempt manual reconnection if automatic reconnect failed
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`Attempting manual reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

        setTimeout(async () => {
          try {
            this.connectionPromise = null;
            await this.startConnection();
          } catch (err) {
            console.error('Manual reconnect failed', err);
          }
        }, 5000);
      }
    });

    // Start connection
    this.connectionPromise = this.connection
      .start()
      .then(() => {
        console.log('DocumentUploadHub connected. ConnectionId:', this.connection?.connectionId);
        this.reconnectAttempts = 0;
      })
      .catch((error) => {
        console.error('Error starting DocumentUploadHub connection:', error);
        this.connectionPromise = null;
        throw error;
      });

    return this.connectionPromise;
  }

  /**
   * Stop the SignalR connection.
   */
  async stopConnection(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.connectionPromise = null;
      console.log('DocumentUploadHub connection stopped');
    }
  }

  /**
   * Join an upload session to receive progress updates.
   * Call this before starting a batch upload.
   */
  async joinUploadSession(uploadSessionId: string): Promise<void> {
    await this.ensureConnected();

    if (this.connection) {
      await this.connection.invoke('JoinUploadSession', uploadSessionId);
      console.log(`Joined upload session: ${uploadSessionId}`);
    }
  }

  /**
   * Leave an upload session when no longer interested in updates.
   */
  async leaveUploadSession(uploadSessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeaveUploadSession', uploadSessionId);
      console.log(`Left upload session: ${uploadSessionId}`);
    }
  }

  /**
   * Subscribe to upload progress events.
   * Returns an unsubscribe function.
   */
  onUploadProgress(
    callback: (data: {
      uploadSessionId: string;
      current: number;
      total: number;
      progress: number;
      message: string;
    }) => void
  ): () => void {
    if (!this.connection) {
      console.warn('Cannot subscribe to UploadProgress: connection not initialized');
      return () => {};
    }

    this.connection.on('UploadProgress', callback);

    // Return unsubscribe function
    return () => {
      this.connection?.off('UploadProgress', callback);
    };
  }

  /**
   * Subscribe to upload completion events.
   * Returns an unsubscribe function.
   */
  onUploadComplete(
    callback: (data: {
      uploadSessionId: string;
      totalFiles: number;
      successfulUploads: number;
      failedUploads: number;
      status: 'success' | 'partial' | 'failed';
    }) => void
  ): () => void {
    if (!this.connection) {
      console.warn('Cannot subscribe to UploadComplete: connection not initialized');
      return () => {};
    }

    this.connection.on('UploadComplete', callback);

    // Return unsubscribe function
    return () => {
      this.connection?.off('UploadComplete', callback);
    };
  }

  /**
   * Subscribe to upload error events.
   * Returns an unsubscribe function.
   */
  onUploadError(
    callback: (data: { uploadSessionId: string; error: string }) => void
  ): () => void {
    if (!this.connection) {
      console.warn('Cannot subscribe to UploadError: connection not initialized');
      return () => {};
    }

    this.connection.on('UploadError', callback);

    // Return unsubscribe function
    return () => {
      this.connection?.off('UploadError', callback);
    };
  }

  /**
   * Get the current connection state.
   */
  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  /**
   * Check if currently connected.
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Ensure connection is established before invoking methods.
   */
  private async ensureConnected(): Promise<void> {
    if (!this.isConnected()) {
      await this.startConnection();
    }
  }
}

// Export singleton instance
export const documentUploadSignalR = new DocumentUploadSignalRService();
